import type { ValueUnion } from '@eturi/ts-helpers'
import type { SSOLoginScheme } from '@op/services'
import type { AppleSignInAPIConfig } from '../../types'

const APPLE_SSO_URL = 'https://appleid.apple.com/auth/authorize'
const APPLE_SSO_BASE_PARAMS = {
	client_id: process.env.OURPACT_CLIENT_ID_APPLE,
	redirect_uri: `https://${location.host}/apple-sso-redirect`,
	response_mode: 'query',
	response_type: 'code',
}

const GOOGLE_SSO_URL = 'https://accounts.google.com/o/oauth2/v2/auth'
const GOOGLE_SSO_BASE_PARAMS = {
	access_type: 'offline',
	client_id: process.env.OURPACT_CLIENT_ID_GOOGLE,
	prompt: 'consent',
	redirect_uri: `https://${location.host}/google-sso-redirect`,
	response_type: 'code',
	scope: 'openid email',
}
export const GOOGLE_POPUP_NAME = 'OPGoogleAuth'

const CONNECT_DEEPLINK_URL = 'opconnect://sso_login'

export const SSOInitUrlState = {
	CONNECT: 'opconnect',
	GOOGLE_POPUP: 'googlepopup',
} as const

export type SSOInitUrlState = ValueUnion<typeof SSOInitUrlState>

/**
 * Simply returns the correct params, so we don't have to export these objects.
 */
export const getSSOClientParams = (type: SSOLoginScheme) =>
	type == 'sso_apple' ? APPLE_SSO_BASE_PARAMS : GOOGLE_SSO_BASE_PARAMS

export const getAppleSSOPopUpParams = () => {
	const { client_id: clientId, redirect_uri: redirectURI } = APPLE_SSO_BASE_PARAMS

	return {
		clientId,
		redirectURI,
		scope: 'name email',
		usePopup: true,
	} satisfies AppleSignInAPIConfig
}

/**
 * Returns the OAuth URL based on the args. If state is provided, it's passed through the full
 * OAuth flow when it redirects back to us.
 */
export const getSSOInitURL = (type: SSOLoginScheme, state?: SSOInitUrlState) => {
	const baseParams = getSSOClientParams(type)
	const ssoUrl = new URL(type == 'sso_apple' ? APPLE_SSO_URL : GOOGLE_SSO_URL)
	const ssoParams = new URLSearchParams({ ...baseParams, ...(state ? { state } : {}) })
	ssoUrl.search = ssoParams.toString()

	return ssoUrl.toString()
}

/**
 * Returns the OurPact Connect protocol URL, used to create a deep link into the Connect app.
 */
export const getConnectDeepLinkUrl = (type: SSOLoginScheme, code: string) => {
	const { client_id, redirect_uri } = getSSOClientParams(type)
	const connectUrl = new URL(CONNECT_DEEPLINK_URL)
	const deepLinkParams = new URLSearchParams({ client_id, redirect_uri, type, code })
	connectUrl.search = deepLinkParams.toString()

	return connectUrl
}
